import * as fs from "node:fs";
import { signAppJwt } from "@m8t-stack/github-app-auth";

export interface BrainAppCreds {
  appId: string;
  slug: string;
  org: string;
  installationId: string;
  pemPath: string;
}

export interface FindExistingDeps {
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Injectable for tests; defaults to the real JWT signer. */
  signJwt?: (args: { appId: string; privateKeyPem: string }) => string;
}

/**
 * Returns the recorded brain-App creds IF a valid App is already installed on
 * `org`: the creds file exists, records the same org, its pem is readable, and
 * the App's JWT still lists an installation on that org. Returns null otherwise
 * — no creds file, wrong org, unreadable pem, or the App was deleted on GitHub
 * (stale creds). Any network/parse error resolves to null (treat as "no usable
 * existing App" so the caller creates a fresh one rather than wrongly skipping).
 *
 * This is the idempotency probe for `m8t brain app-create`: it lets the command
 * detect a pre-existing install and offer keep-vs-new instead of blindly
 * creating a duplicate App.
 */
export async function findValidBrainApp(
  credsPath: string,
  org: string,
  deps: FindExistingDeps = {},
): Promise<BrainAppCreds | null> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const signJwt = deps.signJwt ?? signAppJwt;

  if (!fs.existsSync(credsPath)) return null;

  let creds: Partial<BrainAppCreds>;
  try {
    creds = JSON.parse(fs.readFileSync(credsPath, "utf8")) as Partial<BrainAppCreds>;
  } catch {
    return null;
  }

  if (
    !creds.appId ||
    !creds.pemPath ||
    typeof creds.org !== "string" ||
    creds.org.toLowerCase() !== org.toLowerCase()
  ) {
    return null;
  }
  if (!fs.existsSync(creds.pemPath)) return null;

  try {
    const pem = fs.readFileSync(creds.pemPath, "utf8");
    const jwt = signJwt({ appId: creds.appId, privateKeyPem: pem });
    const res = await fetchImpl("https://api.github.com/app/installations", {
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
        "User-Agent": "m8t",
      },
    });
    if (!res.ok) return null;
    const insts = (await res.json()) as { id: number; account?: { login?: string } }[];
    const hit = insts.find((i) => i.account?.login?.toLowerCase() === org.toLowerCase());
    return hit ? (creds as BrainAppCreds) : null;
  } catch {
    return null;
  }
}
